import { useState, useEffect } from 'react';
import { X, FileDown, ExternalLink, AlertCircle, CheckCircle } from 'lucide-react';
import { request, sheetsApi } from '../lib/api';
import { useTelegram } from '../hooks/useTelegram';

interface ExportKPIModalProps {
  projectId: string;
  projectName: string;
  googleSheetId?: string | null;
  onClose: () => void;
}

export function ExportKPIModal({
  projectId,
  projectName,
  googleSheetId,
  onClose,
}: ExportKPIModalProps) {
  const { hapticFeedback } = useTelegram();
  const [spreadsheetId, setSpreadsheetId] = useState(googleSheetId || '');
  const [sheetName, setSheetName] = useState('KPI Dashboard');
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState<{
    success: boolean;
    url?: string;
    recordsCount?: number;
    error?: string;
    serviceAccountEmail?: string;
  } | null>(null);
  const [serviceAccountEmail, setServiceAccountEmail] = useState<string>('загрузка...');

  // Загружаем email сервисного аккаунта при открытии модального окна
  useEffect(() => {
    const loadServiceAccountEmail = async () => {
      try {
        const response = await sheetsApi.getServiceAccountEmail();
        if (response.serviceAccountEmail) {
          setServiceAccountEmail(response.serviceAccountEmail);
        }
      } catch (error) {
        console.error('Failed to load service account email:', error);
        setServiceAccountEmail('не удалось загрузить');
      }
    };
    
    loadServiceAccountEmail();
  }, []);

  const handleExport = async () => {
    if (!spreadsheetId.trim()) {
      alert('Введите ID таблицы Google Sheets');
      return;
    }

    hapticFeedback('medium');
    setIsExporting(true);
    setExportResult(null);

    try {
      const response = await request<{
        success: boolean;
        spreadsheetUrl: string;
        recordsCount: number;
      }>(`/sheets/kpi/${projectId}`, {
        method: 'POST',
        body: JSON.stringify({
          spreadsheetId: spreadsheetId.trim(),
          sheetName: sheetName.trim() || 'KPI Dashboard',
        }),
      });

      setExportResult({
        success: true,
        url: response.spreadsheetUrl,
        recordsCount: response.recordsCount,
      });

      hapticFeedback('success');
    } catch (error: any) {
      console.error('Export error:', error);
      // Извлекаем email сервисного аккаунта из ответа ошибки, если он есть
      const errorMessage = error?.message || error?.error || 'Произошла ошибка при экспорте';
      const serviceAccountEmailFromError = error?.serviceAccountEmail;
      
      setExportResult({
        success: false,
        error: errorMessage,
        serviceAccountEmail: serviceAccountEmailFromError,
      });
      
      // Если email получен из ошибки, обновляем его для отображения в инструкции
      if (serviceAccountEmailFromError) {
        setServiceAccountEmail(serviceAccountEmailFromError);
      }
      
      hapticFeedback('error');
    } finally {
      setIsExporting(false);
    }
  };

  const extractSpreadsheetId = (input: string) => {
    // Если вставлена полная ссылка, извлекаем ID
    const match = input.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (match) {
      setSpreadsheetId(match[1]);
    } else {
      setSpreadsheetId(input);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
      <div className="bg-white rounded-t-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileDown className="w-6 h-6 text-green-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Экспорт KPI</h2>
              <p className="text-sm text-gray-500">{projectName}</p>
            </div>
          </div>
          <button
            onClick={() => {
              hapticFeedback('light');
              onClose();
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Инструкция */}
          {!exportResult && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
              <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Инструкция по настройке
              </h3>
              <ol className="text-sm text-blue-800 space-y-2 pl-4 list-decimal">
                <li>
                  Создайте таблицу в{' '}
                  <a
                    href="https://sheets.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium"
                  >
                    Google Sheets
                  </a>
                </li>
                <li>Один раз сохраните её ID в настройках проекта (поле \"ID Google таблицы\")</li>
                <li>В этом окне ID подставится автоматически. При необходимости можно заменить его вручную.</li>
                <li>
                  <strong>Важно:</strong> Предоставьте доступ \"Редактор\" для email:
                  <code className="block mt-1 bg-white px-2 py-1 rounded text-xs break-all">
                    {serviceAccountEmail}
                  </code>
                </li>
                <li>Нажмите \"Экспортировать KPI\"</li>
              </ol>
            </div>
          )}

          {/* Результат экспорта */}
          {exportResult && exportResult.success && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-green-900 font-semibold">
                <CheckCircle className="w-5 h-5" />
                Экспорт выполнен успешно!
              </div>
              <p className="text-sm text-green-800">
                Выгружено задач: <strong>{exportResult.recordsCount}</strong>
              </p>
              {exportResult.url && (
                <a
                  href={exportResult.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Открыть таблицу
                </a>
              )}
            </div>
          )}

          {exportResult && !exportResult.success && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-red-900 font-semibold">
                <AlertCircle className="w-5 h-5" />
                Ошибка экспорта
              </div>
              <p className="text-sm text-red-800">{exportResult.error}</p>
              
              {/* Показываем email сервисного аккаунта, если он есть в ошибке */}
              {exportResult.serviceAccountEmail && (
                <div className="bg-white border border-red-300 rounded-lg p-3 mt-3">
                  <p className="text-xs text-red-700 font-medium mb-2">
                    Предоставьте доступ редактора для этого email:
                  </p>
                  <code className="block text-xs text-red-900 break-all bg-red-50 px-2 py-1 rounded">
                    {exportResult.serviceAccountEmail}
                  </code>
                  <p className="text-xs text-red-600 mt-2">
                    В Google таблице нажмите "Поделиться" → вставьте этот email → выберите "Редактор" → "Готово"
                  </p>
                </div>
              )}
              
              <button
                onClick={() => setExportResult(null)}
                className="mt-3 text-sm text-red-600 underline"
              >
                Попробовать снова
              </button>
            </div>
          )}

          {/* Форма */}
          {!exportResult && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ID таблицы Google Sheets или ссылка
                </label>
                <input
                  type="text"
                  value={spreadsheetId}
                  onChange={(e) => extractSpreadsheetId(e.target.value)}
                  placeholder="1A2B3C4D5E6F7G8H9I0... или вставьте полную ссылку"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Пример ссылки: https://docs.google.com/spreadsheets/d/
                  <strong>1A2B3C4D5E...</strong>/edit
                </p>
                {googleSheetId && (
                  <p className="mt-1 text-xs text-green-600">
                    ID из настроек проекта: <strong>{googleSheetId}</strong>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Название листа (опционально)
                </label>
                <input
                  type="text"
                  value={sheetName}
                  onChange={(e) => setSheetName(e.target.value)}
                  placeholder="KPI Dashboard"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Что будет экспортировано */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Что будет экспортировано:</h4>
                <ul className="text-sm text-gray-700 space-y-1 pl-4 list-disc">
                  <li>Сводка KPI (всего задач, выполнено, в процессе, просрочено)</li>
                  <li>Процент выполнения и процент просрочки</li>
                  <li>Детальный список всех задач проекта</li>
                  <li>Информация о протоколах, разделах, ответственных</li>
                  <li>Сроки, статусы, приоритеты</li>
                </ul>
              </div>

              {/* Кнопки */}
              <div className="flex gap-3">
                <button
                  onClick={handleExport}
                  disabled={isExporting || !spreadsheetId.trim()}
                  className="flex-1 bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isExporting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Экспортируем...
                    </>
                  ) : (
                    <>
                      <FileDown className="w-5 h-5" />
                      Экспортировать KPI
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    hapticFeedback('light');
                    onClose();
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  Отмена
                </button>
              </div>
            </>
          )}

          {/* Кнопка закрытия после успешного экспорта */}
          {exportResult && exportResult.success && (
            <button
              onClick={() => {
                hapticFeedback('light');
                onClose();
              }}
              className="w-full px-6 py-3 border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
            >
              Закрыть
            </button>
          )}
        </div>
      </div>
    </div>
  );
}














